import { useMemo } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { ASI_TIMEOUT_PROPERTY_TYPE_ID, CUSTOM_TOKEN_TIMEOUT_PROPERTY_ID } from "@constants/custom-session-timeout";
import { resources } from "@constants/hardcoded-strings";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { GeneralAdvancedValidationErrors } from "@idw-types/common/validation/common/general-advanced-validation-errors";
import { minutesToSeconds, secondsToMinutes } from "@utils/custom-session-timeout/custom-session-timeout-util";
import { PropertiesSectionDescription } from "../properties-section-header/properties-section-description";
import { AxnDocumentationLink } from "@idw-types/data/reference-data/axn/axn-documentation-link";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";
import { isNullOrUndefined } from "@utils/object-util";
import styles from "./custom-session-timeout-container.less";

type CustomSessionTimeoutContainerProps = {
    isDisabled: boolean;
    validationErrors: GeneralAdvancedValidationErrors;
    asiTimeoutPropertyValue: string;
    pageLevel: PageLevelTypes;
    customTokenTimeoutPropertyValue: string;
    inheritedAsiTimeoutPropertyValue: string;
    inheritedCustomTokenTimeoutPropertyValue: string;
    workflowId: string;
    documentationLinks: Array<AxnDocumentationLink>;
    updateProperties: (properties: Array<{ propertyTypeId: string; newValue: string; added: boolean; }>) => void;
}

export const CustomSessionTimeoutContainer = (props: CustomSessionTimeoutContainerProps) => {
    const {
        validationErrors,
        asiTimeoutPropertyValue,
        customTokenTimeoutPropertyValue,
        documentationLinks,
        inheritedAsiTimeoutPropertyValue,
        inheritedCustomTokenTimeoutPropertyValue,
        pageLevel,
        workflowId,
        updateProperties
    } = props;


    const isServiceWithoutWorkflow = useMemo(() => {
        return pageLevel === PageLevelTypes.APIKEY && !workflowId;
    }, [pageLevel, workflowId]);

    const onSessionTimeChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value !== '' ? minutesToSeconds(event.target.value) : '';
        updateProperties([
            { propertyTypeId: CUSTOM_TOKEN_TIMEOUT_PROPERTY_ID, newValue: value, added: true },
            {
                propertyTypeId: ASI_TIMEOUT_PROPERTY_TYPE_ID,
                newValue: value,
                added: true
            },
        ]);
    };

    const sessionTimeout = useMemo(() => {
        if (asiTimeoutPropertyValue && customTokenTimeoutPropertyValue) {
            return secondsToMinutes(`${Math.min(parseInt(asiTimeoutPropertyValue), parseInt(customTokenTimeoutPropertyValue))}`);
        } else if (asiTimeoutPropertyValue) {
            return secondsToMinutes(asiTimeoutPropertyValue);
        } else if (customTokenTimeoutPropertyValue) {
            return secondsToMinutes(customTokenTimeoutPropertyValue);
        } else {
            return "";
        }
    }, [asiTimeoutPropertyValue, customTokenTimeoutPropertyValue]);

    const inheritedSessionTimeout = useMemo(() => {
        if (inheritedAsiTimeoutPropertyValue && inheritedCustomTokenTimeoutPropertyValue) {
            return secondsToMinutes(`${Math.min(parseInt(inheritedAsiTimeoutPropertyValue), parseInt(inheritedCustomTokenTimeoutPropertyValue))}`);
        } else if (inheritedAsiTimeoutPropertyValue) {
            return secondsToMinutes(inheritedAsiTimeoutPropertyValue);
        } else if (inheritedCustomTokenTimeoutPropertyValue) {
            return secondsToMinutes(inheritedCustomTokenTimeoutPropertyValue);
        } else {
            return "";
        }
    }, [inheritedAsiTimeoutPropertyValue, inheritedCustomTokenTimeoutPropertyValue]);

    const isResetDisabled = useMemo(() => {
        return (
            props.isDisabled ||
            pageLevel === PageLevelTypes.WORKFLOW ||
            isServiceWithoutWorkflow ||
            !inheritedSessionTimeout
        );
    }, [props.isDisabled, pageLevel, isServiceWithoutWorkflow, inheritedSessionTimeout]);

    const isInputDisabled = useMemo(() => {
        //disable if workflow is same as service level or if service level is not present
        return (
            props.isDisabled ||
            (pageLevel === PageLevelTypes.APIKEY && !!workflowId && inheritedSessionTimeout === sessionTimeout && !!sessionTimeout) ||
            (pageLevel === PageLevelTypes.APIKEY && !!workflowId && isNullOrUndefined(sessionTimeout) && !!inheritedSessionTimeout)
        );
    }, [
        props.isDisabled,
        pageLevel,
        workflowId,
        inheritedSessionTimeout,
        sessionTimeout
    ]);


    const handleResetSessionTime = () => {
        updateProperties([
            { propertyTypeId: CUSTOM_TOKEN_TIMEOUT_PROPERTY_ID, newValue: null, added: false },
            { propertyTypeId: ASI_TIMEOUT_PROPERTY_TYPE_ID, newValue: null, added: false },
        ]);
    };

    const isSessionTimeoutResetDisabled = useMemo(() => {
        return isResetDisabled || (asiTimeoutPropertyValue === inheritedAsiTimeoutPropertyValue
            && customTokenTimeoutPropertyValue === inheritedCustomTokenTimeoutPropertyValue);
    }, [isResetDisabled, asiTimeoutPropertyValue, inheritedAsiTimeoutPropertyValue, customTokenTimeoutPropertyValue, inheritedCustomTokenTimeoutPropertyValue]);

    const sessionCustomizationResources = resources.pages.verificationServiceDetails.tabs.general.subTabs.advanced.sections.sessionCustomization;

    const documentationLink = useMemo(() => {
        if (documentationLinks.length) {
            const documentationLink = documentationLinks.find(doc => doc.shortName === 'session-settings-widget');
            if (documentationLink) {
                return documentationLink.url;
            }
        }

        return '';
    }, [documentationLinks]);

    return (
        <div>
            <ContentPageHeader title={sessionCustomizationResources.title} />
            <ContentPageBody isMaxWidth={true}>
                <PropertiesSectionDescription description={sessionCustomizationResources.description} documentationLink={documentationLink} />

                <div className={styles.customSessionTimeoutContent}>
                    <ThemingFormGroupWrapper
                        currentThemingPropertyValue={sessionTimeout}
                        inheritedThemingPropertyValue={inheritedSessionTimeout}
                        pageLevel={pageLevel}
                        hideComparison={isServiceWithoutWorkflow || !inheritedSessionTimeout || pageLevel === PageLevelTypes.WORKFLOW}
                    >
                        <InputFormGroup
                            controlId={'session-time-input'}
                            automationId={'session-time-input'}
                            label={sessionCustomizationResources.inputs.asiTimeout.label}
                            ariaLabel={sessionCustomizationResources.inputs.asiTimeout.label}
                            onChange={onSessionTimeChanged}
                            value={sessionTimeout}
                            disabled={isInputDisabled}
                            isRequired={true}
                            layout={LayoutTypes.COLUMN}
                            labelWidth={LabelWidthTypes.LARGE}
                            afterLabelTooltipText={sessionCustomizationResources.inputs.asiTimeout.tooltipText}
                            afterLabelIcon={isSessionTimeoutResetDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetSessionTime}
                        />
                    </ThemingFormGroupWrapper>
                    {validationErrors.asiTimeoutError}
                </div>

            </ContentPageBody>
        </div>)
}